/**
 * PinGate — a simple 4-digit PIN lock over the whole app (temporary access
 * control). Until the correct PIN is entered nothing else renders; on success
 * we remember it for the tab session so it isn't re-asked on every navigation.
 *
 * NOTE: this is a lightweight front-end gate (PIN is hardcoded for now), not real
 * auth — meant to keep the portal closed while it's in private testing.
 */

import { type ReactNode, useState } from 'react';

const PIN = '1359';
const STORAGE_KEY = 'cods_pin_ok';

/** Paths that must stay public (no PIN) — e.g. the customer tracking page. */
function isPublicPath(): boolean {
  return window.location.pathname.startsWith('/t/');
}

export function PinGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(STORAGE_KEY) === '1');
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  // Customer-facing public pages bypass the gate (they never see the portal).
  if (isPublicPath()) return <>{children}</>;
  if (unlocked) return <>{children}</>;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value === PIN) {
      sessionStorage.setItem(STORAGE_KEY, '1');
      setUnlocked(true);
    } else {
      setError(true);
      setValue('');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-xs rounded-2xl border border-white/10 bg-slate-900 p-7 text-center shadow-2xl"
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-2xl">
          🔒
        </div>
        <h1 className="text-lg font-bold text-white">Enter access PIN</h1>
        <p className="mt-1 text-sm text-slate-400">This portal is locked.</p>

        <input
          autoFocus
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          value={value}
          onChange={(e) => {
            setValue(e.target.value.replace(/\D/g, '').slice(0, 4));
            setError(false);
          }}
          placeholder="••••"
          className="mt-5 w-full rounded-xl border border-white/10 bg-slate-800 py-3 text-center text-2xl tracking-[0.5em] text-white outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30"
        />

        {error ? <p className="mt-2 text-sm text-rose-400">Wrong PIN — try again.</p> : null}

        <button
          type="submit"
          disabled={value.length !== 4}
          className="mt-5 w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white transition-colors hover:bg-indigo-500 disabled:opacity-40"
        >
          Unlock
        </button>
      </form>
    </div>
  );
}
